import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { registerSW } from "virtual:pwa-register";

import "@/styles/globals.css";
// Imported separately (not via `@import` inside globals.css) so it bypasses
// Tailwind v4's Lightning CSS pass, which collapses the glass surfaces'
// `backdrop-filter` down to only the `-webkit-` prefixed form and drops the
// standard property — breaking the frosted glass in Firefox and other engines.
import "@/styles/glass.css";
import "@/styles/clay.css";
import { syncViewportMetrics } from "@/lib/viewport";
import { initNativeShell, isNativePlatform } from "@/lib/native";
import { initDensityFromStorage } from "@/context/ThemeContext";
import { AppRoutes } from "@/app/routes";
import { ThemeProvider } from "@/context/ThemeContext";
import { PreferencesUIProvider } from "@/context/PreferencesUIContext";
import { UserPreferencesProvider } from "@/context/UserPreferencesContext";
import { PwaInstallProvider } from "@/context/PwaInstallContext";
import { GlobalSearchProvider } from "@/context/GlobalSearchContext";
import { Auth0ProviderWithNavigate } from "@/auth/Auth0ProviderWithNavigate";
import { UserProvider } from "@/auth/UserContext";

// Configure the native shell (status bar, keyboard, safe-area insets) before
// the first viewport sync so CSS insets are seeded on the first frame. No-op on
// web.
void initNativeShell();
syncViewportMetrics();
initDensityFromStorage();

// Register the PWA service worker in production browser builds only. The plugin
// SW is disabled in dev, and inside a native Capacitor WebView a Workbox SW over
// the capacitor:// scheme fights native caching, so skip it there.
if (import.meta.env.PROD && !isNativePlatform()) {
  registerSW({ immediate: true });
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ThemeProvider>
      <PreferencesUIProvider>
        <PwaInstallProvider>
          <GlobalSearchProvider>
            <BrowserRouter useTransitions={false}>
              <Auth0ProviderWithNavigate>
                <UserProvider>
                  <UserPreferencesProvider>
                    <AppRoutes />
                  </UserPreferencesProvider>
                </UserProvider>
              </Auth0ProviderWithNavigate>
            </BrowserRouter>
          </GlobalSearchProvider>
        </PwaInstallProvider>
      </PreferencesUIProvider>
    </ThemeProvider>
  </StrictMode>,
);
